import {
  Box,
  Button,
  Center,
  Flex,
  Grid,
  GridItem,
  GridProps,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  Tooltip,
  useBoolean,
} from "@chakra-ui/react";
import { observer } from "mobx-react-lite";
import { toJsonBlob, download, Graph } from "@noli/core";
import React, { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { cloneDeep } from "lodash";
import { toJS } from "mobx";
import { useBottomScrollListener } from "react-bottom-scroll-listener";
import { Editor_Words } from "@/utils/lang/editor";
import { Project_Words, useTranslate } from "@/utils/lang";
import { genResponsivePx } from "@/utils/style";
import { formatTime } from "@/utils/format-time";
import { tasksStore } from "@/stores/tasks";
import getScrollBarProps from "@/styles/scrollBarProps";

import { Toast, toast } from "@/components/Toast";
import { More } from "@/components/Icons";
import { DeleteProjectModal } from "../DeleteProjectModal";
import OperationMenu from "@/modules/album/components/OperationMenu";
import { ReplaceProjectCancelTaskRemind } from "@/modules/editor/components/CancelTaskRemind";

import { cloudProjectsManager } from "@/modules/projects/cloudManager";
import { cloudProjectActions } from "@/modules/projects/projectProcessor";
import { loadJson } from "@/modules/projects/utils";
import styles from "./index.module.scss";
import { CloudProjectType } from "@/modules/projects/types/cloud";
import { compressedUrl } from "@/utils/string";

async function onDownload(project: CloudProjectType, toast: string): Promise<void> {
  Toast({
    status: "success",
    title: toast,
  });
  const json = await loadJson(project?.url || project?.content);
  const graph = Graph.fromJsonInfo(json.graphInfo);
  const blob = toJsonBlob(graph.toJson());
  await download(blob, `${project.project_name}.noli`);
}

const IProjectListModal: React.FC<{
  open: boolean;
  onClose: () => void;
  onCreate: () => void;
  onDeleteClose: () => void;
}> = ({ open, onClose, onCreate, onDeleteClose }) => {
  const { translate } = useTranslate();
  const { list = [], total, currentProject } = cloudProjectsManager;
  const [curSelect, setCurSelect] = useState(currentProject);
  const hasMore = !!total && list.length < total;
  const [loadingMore, setLoadingMore] = useState(false);
  const [debounce, setDebounce] = useState(200);
  const [switchLoading, switchLoadingOp] = useBoolean(false);
  const [deleteOpen, deleteOp] = useBoolean(false);
  const [projectToBeDelete, setProjectToBeDelete] = useState<undefined | CloudProjectType>();

  const [cancelTaskModalOpen, cancelTaskModalOp] = useBoolean(false);

  useLayoutEffect(() => {
    if (open) {
      setTimeout(() => {
        setDebounce(800);
      }, 300);
    }
  }, [open]);
  const scrollRef = useBottomScrollListener(
    () => {
      console.log("on subject bottom callback");
      if (!hasMore) return;
      if (loadingMore) return;
      loadMore();
    },
    {
      offset: 10,
      debounce: debounce,
      triggerOnNoScroll: false,
    },
  );

  useEffect(() => {
    if (open) {
      cloudProjectsManager.refreshCloudProjects().then((list) => {
        if (list) {
          setCurSelect(list[0]);
        }
      });
    }
  }, [open]);

  // useEffect(() => {
  //   if (currentProject !== curSelect) {
  //     setCurSelect(currentProject);
  //   }
  // }, [currentProject]);

  function loadMore() {
    setLoadingMore(true);
    return cloudProjectsManager
      .getCloudProjects(true)
      .then((res) => {
        return {
          list: toJS(res?.list),
          total: res?.total,
        };
      })
      .finally(() => {
        setLoadingMore(false);
      });
  }

  const handleClick = (project: CloudProjectType) => {
    onClose();
    handleConfirm(project);
    setCurSelect(project);
  };

  function replaceCloudProject(newProject: CloudProjectType) {
    switchLoadingOp.on();
    cloudProjectActions
      .saveAndReplaceProject(cloneDeep(newProject))
      .then(() => {
        handleCancel();
      })
      .catch((e) => {
        console.error("e: ", e);
      })
      .finally(() => {
        switchLoadingOp.off();
      });
  }
  function handleConfirm(curSelect: CloudProjectType) {
    const newProject = list?.find((project) => curSelect?.project_id === project.project_id);

    if (!newProject) {
      toast({
        title: translate(Editor_Words["select_a_cloud_project"]),
        status: "error",
      });
      return;
    }

    if (tasksStore.batchs.length > 0) {
      cancelTaskModalOp.on();
    } else {
      replaceCloudProject(newProject);
    }
  }

  function handleCancel() {
    onClose();
    setCurSelect(currentProject);
  }

  function handleProjectOp(key: string, id: string) {
    switch (key) {
      case "download":
        {
          const p = list.find((p) => p.project_id === id);
          if (!p) return;
          onDownload(p, translate(Editor_Words["downloading-message"]));
        }
        break;
      case "delete":
        {
          const p = list.find((p) => p.project_id === id);
          setProjectToBeDelete(p);
          handleCancel();
          deleteOp.on();
        }
        break;
      default:
        break;
    }
  }

  const switchDisabled = useMemo(() => {
    return list?.length <= 1 || !curSelect || curSelect.project_id === currentProject?.project_id;
  }, [list, curSelect, currentProject]);

  const getPreview = (preview: string) => {
    const urls = preview.split(",").reverse();
    let props: GridProps = {};
    if (urls.length <= 1) {
      props = {
        gridTemplateAreas: `"image_0"`,
      };
    } else if (urls.length === 2) {
      props = {
        gridTemplateColumns: "66px 66px 1fr",
        gridTemplateRows: "57px",
        gridTemplateAreas: `
          "image_0 image_0 image_1"
          "image_0 image_0 image_1"
          `,
      };
    } else {
      props = {
        gridTemplateColumns: "66px 66px 1fr",
        gridTemplateRows: "57px 1fr",
        gridTemplateAreas: `
            "image_0 image_0 image_1"
            "image_0 image_0 image_2"
            `,
      };
    }
    // 多张预览图

    return (
      <Grid h="100%" w="100%" {...props}>
        {urls.map((url, index) => {
          return (
            <GridItem key={url} gridArea={`image_${index}`}>
              <Image
                h="100%"
                w="100%"
                objectFit="cover"
                src={compressedUrl(url, 320)}
                opacity={0.75}
              />
            </GridItem>
          );
        })}
      </Grid>
    );
  };

  return (
    <>
      <Modal isCentered variant="dark" isOpen={open} onClose={handleCancel}>
        <ModalOverlay />
        <ModalContent w="auto">
          <ModalHeader userSelect="none" textAlign="center" pos="relative" pb="32px">
            {translate(Editor_Words["cloud_project_switch_header"])}
            <Button
              variant="outline"
              pos="absolute"
              left="40px"
              h="42px"
              w="136px"
              onClick={onCreate}
            >
              +{translate(Editor_Words["create_new_project_button_text"])}
            </Button>
          </ModalHeader>
          <ModalCloseButton top="20px" right="20px" />
          <ModalBody>
            <Flex
              w={genResponsivePx(1200)}
              h={genResponsivePx(700)}
              userSelect="none"
              flexWrap="wrap"
              ref={scrollRef as any}
              {...getScrollBarProps({ autoMargin: false })}
            >
              {list.map((project) => {
                const isChecked = curSelect?.project_id === project.project_id;
                return (
                  <Flex
                    className={styles.card}
                    pos="relative"
                    key={project.project_id}
                    w={genResponsivePx(280)}
                    h={genResponsivePx(220)}
                    mb={genResponsivePx(16)}
                    mx="6px"
                    border="2px solid"
                    borderColor={isChecked ? "brand.primary" : "transparent"}
                    rounded="4px"
                    p="4px"
                    flexDirection="column"
                    cursor="pointer"
                    justifyContent="space-around"
                    bg={isChecked ? "fill.button" : "transparent"}
                    _hover={{
                      bg: "#545454",
                      color: "#ffffff",
                    }}
                    onClick={() => handleClick(project)}
                  >
                    <Box w="100%" rounded="4px" h={genResponsivePx(170)} overflow="hidden">
                      {getPreview(project.preview)}
                    </Box>
                    <Flex>
                      <Box w="60%">
                        <Tooltip
                          hasArrow
                          placement="bottom"
                          bg="fill.icon"
                          label={project.project_name}
                        >
                          <Text
                            color={isChecked ? "brand.primary" : "text.subtitle"}
                            whiteSpace="nowrap"
                            textOverflow="ellipsis"
                            overflow="hidden"
                          >
                            {project.project_name}
                          </Text>
                        </Tooltip>
                        <Text
                          fontSize="12px"
                          whiteSpace="nowrap"
                          transform="scale(0.83)"
                          transformOrigin="left"
                        >
                          {formatTime(
                            project.update_time * 1000,
                            translate(Project_Words["cloud_switch_modal_update_time"]).replace(
                              "$",
                              "YYYY-MM-DD HH:mm",
                            ),
                          )}
                        </Text>
                      </Box>
                      <Flex w="40%" align="center" justify="flex-end" fontSize="12px">
                        {isChecked ? (
                          <Text color="brand.primary">
                            {translate(Project_Words["cloud_switch_modal_current_project"])}
                          </Text>
                        ) : (
                          <Button
                            className={styles.switchBtn}
                            variant="outline"
                            w="60px"
                            h="26px"
                            color="text.disabled"
                            borderColor="text.disabled"
                            _hover={{
                              color: "white",
                              borderColor: "text.disabled",
                            }}
                          >
                            {translate(Project_Words["cloud_switch_modal_enter_project"])}
                          </Button>
                        )}
                      </Flex>
                    </Flex>
                    {!isChecked && (
                      <OperationMenu
                        containerRef={scrollRef}
                        onSelect={(key: string) => handleProjectOp(key, project.project_id)}
                        menuList={[
                          {
                            key: "delete",
                            content: translate(Project_Words["cloud_switch_modal_delete_project"]),
                          },
                          {
                            key: "download",
                            content: translate(
                              Project_Words["cloud_switch_modal_download_project"],
                            ),
                          },
                        ]}
                        styles={{
                          menuButton: {
                            pos: "absolute",
                            boxSize: "20px",
                            right: "1em",
                            top: "1em",
                            w: "20px",
                            h: "20px",
                          },
                          menuList: {
                            bg: "bg.panelBg",
                            overflowY: "auto",
                            rounded: "none",
                            border: "none",
                          },
                          menuItem: {
                            px: "12px",
                          },
                        }}
                      >
                        <Box className="moreBtn" boxSize="20px">
                          <More boxSize="20px" />
                        </Box>
                      </OperationMenu>
                    )}
                  </Flex>
                );
              })}
              {/* {loadingMore ? (
                <Center py="12px">
                  <Spinner size="md" color="text.subtitle" />
                </Center>
              ) : null} */}
              {!hasMore ? (
                <Center py="12px" color="text.subtitle" w="100%">
                  {translate(Project_Words.cloud_project_shown_all)}
                </Center>
              ) : null}
            </Flex>
            <Text userSelect="none" textAlign="end" fontSize="12px" my="6px" color="text.subtitle">
              {translate(Project_Words["cloud_switch_modal_load_more"])}
            </Text>
          </ModalBody>
        </ModalContent>
      </Modal>
      <ReplaceProjectCancelTaskRemind
        open={cancelTaskModalOpen}
        onClose={() => cancelTaskModalOp.off()}
        onCancel={() => {
          cancelTaskModalOp.off();
        }}
        onConfirm={() => {
          const newProject = list?.find((project) => curSelect?.project_id === project.project_id);
          if (!newProject) {
            toast({
              title: translate(Editor_Words["select_a_cloud_project"]),
              status: "error",
            });
            return;
          }
          cancelTaskModalOp.off();
          replaceCloudProject(newProject);
        }}
      />
      <DeleteProjectModal
        open={deleteOpen}
        onClose={() => {
          onDeleteClose();
          deleteOp.off();
        }}
        project={projectToBeDelete}
      />
    </>
  );
};

export const ProjectListModal = observer(IProjectListModal);
